import React, { forwardRef } from "react";
import "./InputField.css";
import { Button } from "../Button/Button";
import { Container } from "../Container/Container";

export interface InputFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  showSearchButton?: boolean;
  icon?: React.ReactNode;
  error?: string;
  className?: string;
  style?: React.CSSProperties;
  inputStyle?: string;
  inputIconStyle?: string;
  inputErrorStyle?: string;
  touched?: boolean;
  buttonClick?: () => void;
  uncontrolled?: boolean;
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      showSearchButton = false,
      icon,
      error,
      className = "",
      style,
      inputStyle = "",
      inputIconStyle = "",
      inputErrorStyle = "",
      value,
      defaultValue,
      uncontrolled = false,
      touched,
      buttonClick,
      ...rest
    },
    ref
  ) => {
    const showError = touched && !!error;

    const wrapperModifiers = `input-field-wrapper ${
      inputIconStyle === "icon-outside"
        ? "icon-outside-mode"
        : inputIconStyle === "icon-inside"
          ? "icon-inside-mode"
          : ""
    } ${showError ? "error" : ""}`;

    const inputClass = `rounded-input ${inputStyle} ${showError ? "error-border" : ""}`;

    const valueProps: Pick<
      React.InputHTMLAttributes<HTMLInputElement>,
      "value" | "defaultValue"
    > = uncontrolled
      ? { defaultValue: defaultValue ?? "" }
      : { value: value ?? "" };

    return (
      <Container className={`input-field ${className}`} style={style}>
        <Container className={wrapperModifiers}>
          <input ref={ref} {...rest} {...valueProps} className={inputClass} />

          {icon &&
            (showSearchButton ? (
              <Button
                palette="primary"
                hasIcon={false}
                colorScheme="medium"
                size="small"
                className={`search-icon-wrapper ${inputIconStyle}`}
                onClick={buttonClick}
              >
                {icon}
              </Button>
            ) : (
              <span className={`icon-wrapper ${inputIconStyle}`}>{icon}</span>
            ))}
        </Container>

        {showError && (
          <span className={`input-error ${inputErrorStyle}`}>{error}</span>
        )}
      </Container>
    );
  }
);

/**
 * InputField Component
 *
 * A reusable input component with support for:
 * - Optional icons (inside or outside the input),
 * - Optional search button rendering,
 * - Custom styles and class overrides,
 * - Inline error handling and display,
 * - Forwarded refs for direct input access.
 *
 * ## Features
 * - Shows an icon or a button (based on `showSearchButton`).
 * - Displays an error message when `error` is provided and `touched` is true.
 * - Supports full border style for icon-inside layout and underline for icon-outside.
 * - Uses React.forwardRef to support ref forwarding for direct DOM access.
 *
 * ## Props
 * @param {boolean} [showSearchButton=false] - Whether to show the icon as a button instead of a span.
 * @param {React.ReactNode} [icon] - Icon element to display inside or outside the input.
 * @param {string} [error] - Error message to display below the input.
 * @param {string} [className] - Custom class for the outer wrapper.
 * @param {React.CSSProperties} [style] - Inline styles for the outer wrapper.
 * @param {string} [inputStyle] - Additional class for the `<input>` element.
 * @param {string} [inputIconStyle] - Position style for the icon: "icon-inside" or "icon-outside".
 * @param {string} [inputErrorStyle] - Custom class for the error message.
 * @param {string} [value] - Controlled value of the input.
 * @param {function} [onChange] - Event handler for input change.
 * @param {string} [type] - Input type (e.g., "text", "email").
 * @param {function} [onKeyDown] - Event handler for key press (e.g., Enter).
 * @param {any} [rest] - Any additional props passed to the native `<input>`.
 * @param {boolean} [touched] - Whether the input field has been interacted with.
 *
 * ## Ref Forwarding
 * This component uses React.forwardRef to allow parent components to access the underlying
 * HTMLInputElement. This is useful for focus management, programmatic access, and integration
 * with form libraries.
 *
 * ## Usage
 * @example
 * <InputField
 *   placeholder="Email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   icon={<MailIcon />}
 *   inputIconStyle="icon-inside"
 * />
 *
 * @example
 * <InputField
 *   placeholder="Search..."
 *   value={search}
 *   onChange={handleSearch}
 *   showSearchButton
 *   icon={<SearchIcon />}
 *   inputIconStyle="icon-outside"
 * />
 *
 * @example
 * <InputField
 *   placeholder="Username"
 *   value={username}
 *   onChange={handleUsernameChange}
 *   error="Username is required"
 *   touched={true}
 * />
 *
 * @example
 * // With ref forwarding
 * const inputRef = useRef<HTMLInputElement>(null);
 * <InputField
 *   ref={inputRef}
 *   placeholder="Search"
 *   onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.focus()}
 * />
 */
